import { Link, useLocation, useNavigate } from "react-router-dom";
import { Instagram, Youtube, Linkedin, Podcast } from "lucide-react";
import { XIcon } from "@/lib/socialPlatforms";
import { usePodgoVersion } from "@/hooks/usePodgoVersion";
import { useBugReport } from "@/contexts/BugReportContext";

const footerLinks = {
  product: {
    title: "Product",
    links: [
      { label: "Features", href: "#features" },
      { label: "Pricing", href: "#pricing" },
      { label: "How It Works", href: "#how-it-works" },
      { label: "API (Coming Soon)", href: "#" },
    ],
  },
  resources: {
    title: "Resources",
    links: [
      { label: "Help Center", href: "/help", isRoute: true },
      { label: "News", href: "/blog", isRoute: true },
      { label: "Report a Bug", href: "#bug-report", isBugReport: true },
    ],
  },
  company: {
    title: "Company",
    links: [
      { label: "About", href: "/about", isRoute: true },
      { label: "Careers", href: "/careers", isRoute: true },
      { label: "Contact", href: "/help", isRoute: true },
      { label: "Partners", href: "#" },
    ],
  },
  legal: {
    title: "Legal",
    links: [
      { label: "Privacy Policy", href: "/privacy", isRoute: true },
      { label: "Terms of Service", href: "/terms", isRoute: true },
      { label: "DMCA / Copyright Policy", href: "/dmca", isRoute: true },
      { label: "Cookie Policy", href: "/cookies", isRoute: true },
      { label: "GDPR Compliance", href: "/gdpr", isRoute: true },
      { label: "Ads.txt", href: "/ads.txt", isExternal: true },
    ],
  },
};

const socialLinks = [
  { label: "YouTube", href: "https://youtube.com/@podgopodcasts", icon: Youtube },
  { label: "X", href: "https://x.com/podgoservices", icon: XIcon },
  { label: "Instagram", href: "https://www.instagram.com/podgoservices", icon: Instagram },
  { label: "LinkedIn", href: "https://www.linkedin.com/company/podgo-io/", icon: Linkedin },
];

export function MarketingFooter() {
  const { version } = usePodgoVersion();
  const location = useLocation();
  const navigate = useNavigate();
  const { launch: launchBugReport } = useBugReport();

  const handleHashLink = (href: string) => {
    if (!href.startsWith("#")) return;
    if (location.pathname === "/") {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/" + href);
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 400);
    }
  };

  return (
    <footer className="bg-primary border-t border-secondary/10">
      <div className="container mx-auto px-4 md:px-6 py-12 md:py-16">
        {/* Main footer content */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 md:gap-12 mb-12">
          {/* Brand column */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center">
                <Podcast className="h-4 w-4 text-accent-foreground" aria-hidden="true" />
              </div>
              <span className="text-xl font-bold text-secondary">PODGO</span>
            </Link>
            <p className="text-secondary/60 text-sm leading-relaxed mb-4">
              The unified podcast operations platform. Distribute, track, and grow your podcast everywhere.
            </p>
            {/* Social links */}
            <div className="flex gap-4">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center text-secondary/60 hover:bg-secondary/20 hover:text-secondary transition-colors"
                    aria-label={`${social.label} (opens in new tab)`}
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Link columns */}
          {Object.values(footerLinks).map((section) => (
            <div key={section.title}>
              <p className="font-semibold text-secondary mb-4 text-sm">
                {section.title}
              </p>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.label}>
                    {(link as any).isBugReport ? (
                      <button
                        onClick={launchBugReport}
                        className="text-secondary/60 hover:text-secondary text-sm transition-colors text-left"
                      >
                        {link.label}
                      </button>
                    ) : (link as any).isRoute ? (
                      <Link
                        to={link.href}
                        onClick={() => window.scrollTo({ top: 0 })}
                        className="text-secondary/60 hover:text-secondary text-sm transition-colors"
                      >
                        {link.label}
                      </Link>
                    ) : (link as any).isExternal ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-secondary/60 hover:text-secondary text-sm transition-colors"
                      >
                        {link.label}
                        <span className="sr-only"> (opens in new tab)</span>
                      </a>
                    ) : (
                      <button
                        onClick={() => handleHashLink(link.href)}
                        className="text-secondary/60 hover:text-secondary text-sm transition-colors text-left"
                      >
                        {link.label}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-secondary/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-center md:text-left">
            <p className="text-secondary/50 text-sm">
              © {new Date().getFullYear()} PODGO. All rights reserved.
            </p>
            <p className="text-secondary/60 text-xs mt-1">
              PODGO Beta v{version}
            </p>
          </div>
          <p className="text-secondary/60 text-xs">
            Made with ❤️ for podcasters everywhere
          </p>
        </div>
      </div>
    </footer>
  );
}
